(function () {
  'use strict';

  var STORAGE_KEY = 'immunosciences_daily_tracker_v1';

  var AVATAR_PALETTE = [
    ['#e1f5ee', '#085041'],
    ['#eeedfe', '#3c3489'],
    ['#faece7', '#712b13'],
    ['#e6f1fb', '#0c447c'],
    ['#fbeaf0', '#72243e'],
    ['#eaf3de', '#27500a'],
    ['#faeeda', '#633806']
  ];

  var MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  var DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  function pad2(n) {
    return String(n).padStart(2, '0');
  }

  function formatDate(d) {
    return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate());
  }

  function todayStr() {
    return formatDate(new Date());
  }

  function parseDate(str) {
    var p = str.split('-');
    return new Date(parseInt(p[0], 10), parseInt(p[1], 10) - 1, parseInt(p[2], 10));
  }

  function daysSince(dateStr) {
    var d = parseDate(dateStr);
    var now = new Date();
    now.setHours(0, 0, 0, 0);
    d.setHours(0, 0, 0, 0);
    return Math.max(0, Math.floor((now - d) / 86400000));
  }

  function ritualDay(joinDate) {
    if (!joinDate) return 1;
    return Math.min(daysSince(joinDate) + 1, 30);
  }

  function timeAgo(iso) {
    var diff = Date.now() - new Date(iso).getTime();
    var mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return mins + 'm ago';
    var hrs = Math.floor(mins / 60);
    if (hrs < 24) return hrs + 'h ago';
    return Math.floor(hrs / 24) + 'd ago';
  }

  function avatarColors(name) {
    var h = 0;
    for (var i = 0; i < name.length; i++) {
      h = (h + name.charCodeAt(i)) % AVATAR_PALETTE.length;
    }
    return AVATAR_PALETTE[h];
  }

  function feedActionText(action, city) {
    switch (action) {
      case 'joined':
        return 'joined the community wall';
      case 'logged':
        return 'just logged their morning ritual';
      case 'streak_milestone':
        return 'hit a streak milestone';
      default:
        return 'joined the ritual';
    }
  }

  function qs(root, sel) {
    return root.querySelector(sel);
  }

  function qsa(root, sel) {
    return Array.prototype.slice.call(root.querySelectorAll(sel));
  }

  /* ── Storage ── */
  var DailyTrackerStorage = {
    read: function () {
      try {
        var raw = localStorage.getItem(STORAGE_KEY);
        if (raw) return JSON.parse(raw);
      } catch (e) { /* ignore */ }
      return null;
    },
    write: function (data) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      } catch (e) { /* ignore */ }
    },
    clear: function () {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (e) { /* ignore */ }
    },
    savePartial: function (profile, supplementsTaken) {
      var data = DailyTrackerStorage.read() || {};
      data.userId = profile.userId;
      data.firstName = profile.firstName;
      data.city = profile.city;
      data.joinDate = profile.joinDate;
      data.todayPartial = { date: todayStr(), supplementsTaken: supplementsTaken };
      DailyTrackerStorage.write(data);
    },
    clearPartial: function () {
      var data = DailyTrackerStorage.read();
      if (!data) return;
      delete data.todayPartial;
      DailyTrackerStorage.write(data);
    },
    getPartial: function () {
      var data = DailyTrackerStorage.read();
      if (!data || !data.todayPartial) return null;
      if (data.todayPartial.date !== todayStr()) return null;
      return data.todayPartial.supplementsTaken || [];
    }
  };

  /* ── API ── */
  function DailyTrackerApi(baseUrl) {
    this.baseUrl = (baseUrl || '').replace(/\/$/, '');
  }

  DailyTrackerApi.prototype.request = function (path, options) {
    var self = this;
    var url = self.baseUrl + path;
    var opts = options || {};
    var method = opts.method || 'GET';
    var headers = Object.assign({}, opts.headers || {});
    if (opts.body) {
      headers['Content-Type'] = 'application/json';
    }
    if (/ngrok/i.test(self.baseUrl)) {
      headers['ngrok-skip-browser-warning'] = 'true';
    }
    return fetch(url, {
      method: method,
      headers: headers,
      body: opts.body ? JSON.stringify(opts.body) : undefined
    }).then(function (res) {
      return res.text().then(function (text) {
        var body;
        try {
          body = text ? JSON.parse(text) : {};
        } catch (e) {
          var err = new Error('API returned non-JSON from ' + url);
          err.cause = text.slice(0, 120);
          throw err;
        }
        return { status: res.status, body: body };
      });
    });
  };

  DailyTrackerApi.prototype.join = function (firstName, city) {
    return this.request('/api/v1/users/join', {
      method: 'POST',
      body: { firstName: firstName, city: city }
    });
  };

  DailyTrackerApi.prototype.getState = function (userId) {
    return this.request('/api/v1/users/' + encodeURIComponent(userId) + '/state');
  };

  DailyTrackerApi.prototype.createLog = function (userId, date, supplementsTaken) {
    return this.request('/api/v1/logs', {
      method: 'POST',
      body: { userId: userId, date: date, supplementsTaken: supplementsTaken }
    });
  };

  DailyTrackerApi.prototype.getStats = function () {
    return this.request('/api/v1/community/stats');
  };

  DailyTrackerApi.prototype.getFeed = function (limit) {
    return this.request('/api/v1/community/feed?limit=' + (limit || 50));
  };

  /* ── State ── */
  function DailyTrackerState() {
    this.profile = null;
    this.currentStreak = 0;
    this.longestStreak = 0;
    this.loggedDates = [];
    this.stats = null;
    this.feed = [];
    this.suppChecked = [false, false, false, false];
    this.loggedToday = false;
    this.loading = true;
    this.lastError = null;
  }

  DailyTrackerState.prototype.hydrateFromStorage = function () {
    var stored = DailyTrackerStorage.read();
    if (!stored || !stored.userId) return false;
    this.profile = {
      userId: stored.userId,
      firstName: stored.firstName,
      city: stored.city || 'India',
      joinDate: stored.joinDate
    };
    return true;
  };

  DailyTrackerState.prototype.applyServerState = function (data) {
    this.currentStreak = data.currentStreak || 0;
    this.longestStreak = data.longestStreak || 0;
    this.loggedDates = data.loggedDates || [];
    this.loggedToday = this.loggedDates.indexOf(todayStr()) >= 0;
  };

  DailyTrackerState.prototype.restorePartial = function () {
    if (this.loggedToday) return;
    var partial = DailyTrackerStorage.getPartial();
    if (!partial) return;
    for (var i = 0; i < 4; i++) {
      this.suppChecked[i] = partial.indexOf(i) >= 0;
    }
  };

  DailyTrackerState.prototype.getSelectedIndices = function () {
    var out = [];
    for (var i = 0; i < 4; i++) {
      if (this.suppChecked[i]) out.push(i);
    }
    return out;
  };

  DailyTrackerState.prototype.dayOfRitual = function () {
    return ritualDay(this.profile && this.profile.joinDate);
  };

  function networkErrorMessage(apiBaseUrl) {
    var pageHttps = window.location.protocol === 'https:';
    var apiLocalHttp = /^http:\/\/(127\.0\.0\.1|localhost)(:\d+)?/i.test(apiBaseUrl || '');
    if (pageHttps && apiLocalHttp) {
      return 'Cannot reach local API from Theme Editor (HTTPS). Use shopify theme dev at http://127.0.0.1:9292, or point API base URL to an HTTPS tunnel (e.g. ngrok).';
    }
    return 'Network error — is the API running at ' + (apiBaseUrl || 'the configured URL') + '?';
  }

  /* ── Toast helper ── */
  function showToast(root, message, isError, onRetry) {
    var toast = qs(root, '[data-dt-toast]') || qs(document.body, '[data-dt-toast]');
    if (!toast) return;
    toast.textContent = '';
    toast.classList.remove('daily-tracker__toast--error', 'is-visible');

    var span = document.createElement('span');
    span.textContent = message;
    toast.appendChild(span);

    if (isError && onRetry) {
      toast.classList.add('daily-tracker__toast--error');
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'daily-tracker__toast-retry';
      btn.textContent = 'Retry';
      btn.addEventListener('click', function () {
        toast.classList.remove('is-visible');
        onRetry();
      });
      toast.appendChild(btn);
    }

    toast.classList.add('is-visible');
    clearTimeout(toast._dtTimer);
    toast._dtTimer = setTimeout(function () {
      toast.classList.remove('is-visible');
    }, isError ? 5000 : 3200);
  }

  function showMilestone(root, streak) {
    var modal = qs(root, '[data-dt-milestone]');
    if (!modal) return;
    var title = qs(modal, '[data-dt-milestone-title]');
    var text = qs(modal, '[data-dt-milestone-text]');
    if (title) title.textContent = streak + '-day streak!';
    if (text) text.textContent = 'You showed up ' + streak + ' days in a row. That is the ritual.';
    modal.classList.remove('is-hidden');
  }

  /* ── Full UI ── */
  function DailyTrackerFullUI(root, state, api, config) {
    this.root = root;
    this.state = state;
    this.api = api;
    this.config = config;
    this.onboardDismissed = false;
    this.onboardKeyHandler = null;
    this.bindEvents();
  }

  DailyTrackerFullUI.prototype.bindEvents = function () {
    var self = this;
    var joinBtn = qs(self.root, '[data-dt-join-btn]');
    if (joinBtn) {
      joinBtn.addEventListener('click', function () {
        self.handleJoin();
      });
    }

    var logBtn = qs(self.root, '[data-dt-log-btn]');
    if (logBtn) {
      logBtn.addEventListener('click', function () {
        self.handleLog();
      });
    }

    qsa(self.root, '[data-dt-supp-item]').forEach(function (item) {
      item.addEventListener('click', function () {
        var idx = parseInt(item.getAttribute('data-dt-supp-item'), 10);
        self.toggleSupp(idx);
      });
    });

    var dismiss = qs(self.root, '[data-dt-milestone-dismiss]');
    if (dismiss) {
      dismiss.addEventListener('click', function () {
        var modal = qs(self.root, '[data-dt-milestone]');
        if (modal) modal.classList.add('is-hidden');
      });
    }

    var onboardClose = qs(self.root, '[data-dt-onboard-close]');
    if (onboardClose) {
      onboardClose.addEventListener('click', function () {
        self.dismissOnboard();
      });
    }

    var onboard = qs(self.root, '[data-dt-onboard]');
    if (onboard) {
      onboard.addEventListener('click', function (e) {
        if (e.target === onboard) self.dismissOnboard();
      });
    }
  };

  DailyTrackerFullUI.prototype.handleJoin = function () {
    var self = this;
    var nameInput = qs(self.root, '[data-dt-name-input]');
    var cityInput = qs(self.root, '[data-dt-city-input]');
    var joinBtn = qs(self.root, '[data-dt-join-btn]');
    var firstName = nameInput ? nameInput.value.trim() : '';
    var city = cityInput ? cityInput.value.trim() : '';
    if (!firstName) {
      if (nameInput) nameInput.focus();
      return;
    }
    if (joinBtn) joinBtn.disabled = true;

    self.api.join(firstName, city || 'India').then(function (res) {
      if (joinBtn) joinBtn.disabled = false;
      if (!res.body.success) {
        showToast(self.root, res.body.error && res.body.error.message || 'Could not join', true, function () {
          self.handleJoin();
        });
        return;
      }
      var data = res.body.data;
      DailyTrackerStorage.write({
        userId: data.userId,
        firstName: firstName,
        city: city || 'India',
        joinDate: data.joinDate
      });
      self.state.profile = {
        userId: data.userId,
        firstName: firstName,
        city: city || 'India',
        joinDate: data.joinDate
      };
      self.hideOnboard();
      showToast(self.root, 'Welcome to the ritual, ' + firstName + ' 🌿');
      self.refreshAll();
    }).catch(function () {
      if (joinBtn) joinBtn.disabled = false;
      showToast(self.root, networkErrorMessage(self.api.baseUrl), true, function () {
        self.handleJoin();
      });
    });
  };

  DailyTrackerFullUI.prototype.toggleSupp = function (idx) {
    if (this.state.loggedToday) return;
    this.state.suppChecked[idx] = !this.state.suppChecked[idx];
    this.renderSupplements();
    this.renderLogButton();
  };

  DailyTrackerFullUI.prototype.handleLog = function () {
    var self = this;
    if (!self.state.profile || !self.state.profile.userId) {
      self.showOnboard(true);
      return;
    }
    var indices = self.state.getSelectedIndices();
    if (!indices.length) {
      showToast(self.root, 'Tap the supplements you\'ve taken');
      return;
    }

    var logBtn = qs(self.root, '[data-dt-log-btn]');
    if (logBtn) logBtn.disabled = true;

    self.api.createLog(self.state.profile.userId, todayStr(), indices).then(function (res) {
      if (logBtn) logBtn.disabled = false;
      if (!res.body.success) {
        showToast(self.root, res.body.error && res.body.error.message || 'Could not save log', true, function () {
          self.handleLog();
        });
        return;
      }

      var data = res.body.data;
      var allTaken = indices.length === 4;

      if (allTaken) {
        DailyTrackerStorage.clearPartial();
      } else {
        DailyTrackerStorage.savePartial(self.state.profile, indices);
      }

      if (self.state.loggedDates.indexOf(todayStr()) < 0) {
        self.state.loggedDates.push(todayStr());
      }
      self.state.loggedToday = true;
      self.state.currentStreak = data.updatedStreak;

      self.renderAll();

      var msg = allTaken
        ? 'Perfect day — all 4 taken. Streak: ' + data.updatedStreak + ' days'
        : 'Logged — streak: ' + data.updatedStreak + ' days';
      showToast(self.root, msg);
      if (data.isMilestone) {
        showMilestone(self.root, data.updatedStreak);
      }
      self.api.getFeed(50).then(function (feedRes) {
        if (feedRes.body.success) {
          self.state.feed = feedRes.body.data || [];
          self.renderFeed();
          self.renderAvatarWall();
        }
      }).catch(function () { /* ignore */ });
    }).catch(function () {
      if (logBtn) logBtn.disabled = false;
      showToast(self.root, networkErrorMessage(self.api.baseUrl), true, function () {
        self.handleLog();
      });
    });
  };

  DailyTrackerFullUI.prototype.showOnboard = function (force) {
    if (!force && this.onboardDismissed) return;
    var onboard = qs(this.root, '[data-dt-onboard]');
    if (!onboard) return;
    onboard.classList.remove('is-hidden');
    this.bindOnboardKeyHandler();
  };

  DailyTrackerFullUI.prototype.hideOnboard = function () {
    var onboard = qs(this.root, '[data-dt-onboard]');
    if (!onboard) return;
    onboard.classList.add('is-hidden');
    this.unbindOnboardKeyHandler();
  };

  DailyTrackerFullUI.prototype.dismissOnboard = function () {
    this.onboardDismissed = true;
    this.hideOnboard();
  };

  DailyTrackerFullUI.prototype.bindOnboardKeyHandler = function () {
    var self = this;
    if (self.onboardKeyHandler) return;
    self.onboardKeyHandler = function (e) {
      if (e.key !== 'Escape') return;
      var onboard = qs(self.root, '[data-dt-onboard]');
      if (onboard && !onboard.classList.contains('is-hidden')) {
        self.dismissOnboard();
      }
    };
    document.addEventListener('keydown', self.onboardKeyHandler);
  };

  DailyTrackerFullUI.prototype.unbindOnboardKeyHandler = function () {
    if (!this.onboardKeyHandler) return;
    document.removeEventListener('keydown', this.onboardKeyHandler);
    this.onboardKeyHandler = null;
  };

  DailyTrackerFullUI.prototype.renderAll = function () {
    this.renderHero();
    this.renderStats();
    this.renderStreak();
    this.renderLogSection();
    this.renderSupplements();
    this.renderLogButton();
    this.renderCalendar();
    this.renderFeed();
    this.renderAvatarWall();
    this.setLoading(false);
  };

  DailyTrackerFullUI.prototype.refreshAll = function () {
    var self = this;
    self.setLoading(true);
    var profile = self.state.profile;
    if (!profile || !profile.userId) {
      self.showOnboard();
      self.setLoading(false);
      return Promise.resolve();
    }

    return self.api.getState(profile.userId).then(function (res) {
      if (res.status === 404 || !res.body.success) {
        DailyTrackerStorage.clear();
        self.state.profile = null;
        self.showOnboard();
        self.setLoading(false);
        return;
      }
      self.state.applyServerState(res.body.data);
      self.state.restorePartial();
      self.hideOnboard();
      try {
        self.renderAll();
      } catch (renderErr) {
        console.error('[DailyTracker] renderAll failed after state load', renderErr);
      }

      return Promise.allSettled([
        self.api.getStats(),
        self.api.getFeed(50)
      ]).then(function (results) {
        if (results[0].status === 'fulfilled' && results[0].value.body.success) {
          self.state.stats = results[0].value.body.data;
        }
        if (results[1].status === 'fulfilled' && results[1].value.body.success) {
          self.state.feed = results[1].value.body.data || [];
        }
        try {
          self.renderAll();
        } catch (renderErr) {
          console.error('[DailyTracker] renderAll failed after community load', renderErr);
        }
        self.setLoading(false);
      });
    }).catch(function (err) {
      console.error('[DailyTracker] refreshAll failed', err);
      showToast(self.root, 'Could not load tracker — check API is running', true, function () {
        self.refreshAll();
      });
      self.setLoading(false);
    });
  };

  DailyTrackerFullUI.prototype.setLoading = function (on) {
    this.state.loading = on;
    this.root.classList.toggle('is-loading', on);
  };

  DailyTrackerFullUI.prototype.renderHero = function () {
    var stats = this.state.stats;
    var liveNum = qs(this.root, '[data-dt-live-num]');
    var heroBadge = qs(this.root, '[data-dt-hero-badge]');
    var cityCount = qs(this.root, '[data-dt-city-count]');
    var doneCount = qs(this.root, '[data-dt-done-count]');

    if (liveNum && stats) {
      liveNum.textContent = (stats.totalLiveCount || 0).toLocaleString();
    }
    if (heroBadge && stats) {
      heroBadge.textContent = 'Join ' + (stats.totalLiveCount || 0).toLocaleString() + '+ people worldwide';
    }
    if (cityCount && stats && this.state.profile) {
      var city = this.state.profile.city;
      var count = (stats.cityCount && stats.cityCount[city]) || 0;
      cityCount.textContent = count > 0 ? count + ' in ' + city : 'Join from your city';
    }
    if (doneCount && stats) {
      doneCount.textContent = (stats.totalLiveCount || 0).toLocaleString();
    }
    var onboardCopy = qs(this.root, '[data-dt-onboard-copy]');
    if (onboardCopy && stats) {
      onboardCopy.textContent =
        (stats.totalLiveCount || 0).toLocaleString() +
        ' people are already on their daily me journey. Enter your name to join the community wall and start your streak today.';
    }
  };

  DailyTrackerFullUI.prototype.renderStats = function () {
    var myStreak = qs(this.root, '[data-dt-my-streak]');
    var globalStreak = qs(this.root, '[data-dt-global-streak]');
    var dayNum = qs(this.root, '[data-dt-day-num]');
    var topStreak = qs(this.root, '[data-dt-top-streak]');

    if (myStreak) myStreak.textContent = String(this.state.currentStreak);
    if (globalStreak && this.state.stats) {
      globalStreak.textContent = String(Math.round(this.state.stats.globalAvgStreak || 0));
    }
    if (dayNum) dayNum.textContent = String(this.state.dayOfRitual());
    if (topStreak) topStreak.textContent = String(this.state.longestStreak || this.state.currentStreak);
  };

  DailyTrackerFullUI.prototype.renderStreak = function () {
    var bigStreak = qs(this.root, '[data-dt-big-streak]');
    if (bigStreak) bigStreak.textContent = String(this.state.currentStreak);

    var dotsWrap = qs(this.root, '[data-dt-week-dots]');
    if (!dotsWrap) return;
    dotsWrap.innerHTML = '';

    var now = new Date();
    now.setHours(0, 0, 0, 0);
    var dow = now.getDay();
    var mondayOffset = dow === 0 ? -6 : 1 - dow;
    var monday = new Date(now);
    monday.setDate(now.getDate() + mondayOffset);

    for (var i = 0; i < 7; i++) {
      var d = new Date(monday);
      d.setDate(monday.getDate() + i);
      d.setHours(0, 0, 0, 0);
      var dStr = formatDate(d);
      var dot = document.createElement('div');
      dot.className = 'daily-tracker__wdot';
      if (d.getTime() === now.getTime()) {
        if (this.state.loggedDates.indexOf(dStr) >= 0) {
          dot.classList.add('is-done');
        } else {
          dot.classList.add('is-today');
        }
      } else if (d.getTime() < now.getTime()) {
        if (this.state.loggedDates.indexOf(dStr) >= 0) {
          dot.classList.add('is-done');
        } else if (this.state.profile && this.state.profile.joinDate && d >= parseDate(this.state.profile.joinDate)) {
          dot.classList.add('is-missed');
        }
      }
      dotsWrap.appendChild(dot);
    }
  };

  DailyTrackerFullUI.prototype.renderLogSection = function () {
    var logCard = qs(this.root, '[data-dt-log-card]');
    var doneBanner = qs(this.root, '[data-dt-done-banner]');
    var logDate = qs(this.root, '[data-dt-log-date]');
    var dayBadge = qs(this.root, '[data-dt-day-badge]');
    var now = new Date();

    if (logDate) {
      logDate.textContent = DAYS[now.getDay()] + ', ' + now.getDate() + ' ' + MONTHS[now.getMonth()];
    }
    if (dayBadge) {
      var day = this.state.dayOfRitual();
      dayBadge.innerHTML = day + '<span>of 30</span>';
    }
    if (logCard) logCard.classList.toggle('is-hidden', this.state.loggedToday);
    if (doneBanner) doneBanner.classList.toggle('is-visible', this.state.loggedToday);
  };

  DailyTrackerFullUI.prototype.renderSupplements = function () {
    var self = this;
    qsa(self.root, '[data-dt-supp-item]').forEach(function (item) {
      var idx = parseInt(item.getAttribute('data-dt-supp-item'), 10);
      var check = qs(item, '[data-dt-supp-check]');
      if (check) {
        check.classList.toggle('is-checked', self.state.suppChecked[idx]);
      }
    });
  };

  DailyTrackerFullUI.prototype.renderLogButton = function () {
    var btn = qs(this.root, '[data-dt-log-btn]');
    if (!btn || this.state.loggedToday) return;
    var any = this.state.suppChecked.some(Boolean);
    var all = this.state.suppChecked.every(Boolean);
    var n = this.state.getSelectedIndices().length;
    btn.classList.toggle('is-ready', any);
    if (all) {
      btn.textContent = 'Log today — all 4 taken ✓';
    } else if (any) {
      btn.textContent = 'Log today — ' + n + ' of 4 taken';
    } else {
      btn.textContent = 'Select what you\'ve taken';
    }
  };

  DailyTrackerFullUI.prototype.renderCalendar = function () {
    var grid = qs(this.root, '[data-dt-cal-grid]');
    var calMonth = qs(this.root, '[data-dt-cal-month]');
    if (!grid) return;

    var now = new Date();
    if (calMonth) calMonth.textContent = MONTHS[now.getMonth()] + ' ' + now.getFullYear();

    grid.innerHTML = '';
    var dayLabels = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
    dayLabels.forEach(function (l) {
      var el = document.createElement('div');
      el.className = 'daily-tracker__cal-day-lbl';
      el.textContent = l;
      grid.appendChild(el);
    });

    var joinDate = this.state.profile && this.state.profile.joinDate
      ? parseDate(this.state.profile.joinDate)
      : new Date();
    joinDate.setHours(0, 0, 0, 0);

    var startDay = new Date(joinDate);
    var dow = startDay.getDay();
    var diff = dow === 0 ? -6 : 1 - dow;
    startDay.setDate(startDay.getDate() + diff);

    var today = new Date();
    today.setHours(0, 0, 0, 0);
    var joinMs = joinDate.getTime();
    var todayMs = today.getTime();

    for (var i = 0; i < 35; i++) {
      var d = new Date(startDay);
      d.setDate(startDay.getDate() + i);
      var dMs = d.getTime();
      var dayEl = document.createElement('div');
      dayEl.className = 'daily-tracker__cal-day';
      dayEl.textContent = String(d.getDate());
      var dStr = formatDate(d);

      if (dMs < joinMs) {
        dayEl.classList.add('is-future');
        dayEl.style.opacity = '0.15';
      } else if (dMs > todayMs) {
        dayEl.classList.add('is-future');
      } else if (dMs === todayMs) {
        dayEl.classList.add('is-today');
        if (this.state.loggedDates.indexOf(dStr) >= 0) {
          dayEl.classList.add('is-done');
        }
      } else if (this.state.loggedDates.indexOf(dStr) >= 0) {
        dayEl.classList.add('is-done');
      } else if (dMs >= joinMs) {
        dayEl.classList.add('is-missed');
      }
      grid.appendChild(dayEl);
    }
  };

  DailyTrackerFullUI.prototype.renderFeed = function () {
    var feed = qs(this.root, '[data-dt-feed]');
    if (!feed) return;
    feed.innerHTML = '';

    var items = this.state.feed || [];
    if (!items.length) {
      var empty = document.createElement('div');
      empty.className = 'daily-tracker__empty';
      empty.textContent = 'Nothing happening yet — be the first to log today!';
      feed.appendChild(empty);
      return;
    }

    items.slice(0, 12).forEach(function (item) {
      var colors = avatarColors(item.name || '?');
      var row = document.createElement('div');
      row.className = 'daily-tracker__feed-item';
      row.innerHTML =
        '<div class="daily-tracker__feed-av" style="background:' + colors[0] + ';color:' + colors[1] + '">' +
        (item.name ? item.name[0].toUpperCase() : '?') +
        '</div>' +
        '<div class="daily-tracker__feed-body">' +
        '<div class="daily-tracker__feed-name"><strong>' + (item.name || 'Someone') + '</strong></div>' +
        '<div class="daily-tracker__feed-sub">' + feedActionText(item.action, item.city) + '</div>' +
        '</div>' +
        '<div class="daily-tracker__feed-time">' + timeAgo(item.timestamp) + '</div>';
      feed.appendChild(row);
    });
  };

  DailyTrackerFullUI.prototype.renderAvatarWall = function () {
    var grid = qs(this.root, '[data-dt-avatar-grid]');
    if (!grid) return;
    grid.innerHTML = '';

    var profile = this.state.profile;
    if (profile && profile.firstName) {
      var you = document.createElement('div');
      you.className = 'daily-tracker__av daily-tracker__av--you';
      you.textContent = profile.firstName[0].toUpperCase();
      you.title = profile.firstName;
      if (!this.state.loggedToday) you.style.display = 'none';
      grid.appendChild(you);
    }

    var seen = {};
    var count = 0;
    (this.state.feed || []).forEach(function (item) {
      if (!item.name || seen[item.name]) return;
      seen[item.name] = true;
      if (profile && item.name === profile.firstName) return;
      var colors = avatarColors(item.name);
      var av = document.createElement('div');
      av.className = 'daily-tracker__av';
      av.style.background = colors[0];
      av.style.color = colors[1];
      av.textContent = item.name[0].toUpperCase();
      av.title = item.name;
      grid.appendChild(av);
      count++;
      if (count >= 24) return;
    });

    if (count >= 20) {
      var more = document.createElement('div');
      more.className = 'daily-tracker__av daily-tracker__av--more';
      more.textContent = '+' + Math.max(0, (this.state.stats && this.state.stats.totalLiveCount || 0) - count);
      grid.appendChild(more);
    }
  };

  DailyTrackerFullUI.prototype.updateStats = function (stats) {
    this.state.stats = stats;
    this.renderHero();
    this.renderStats();
  };

  DailyTrackerFullUI.prototype.updateFeed = function (feed) {
    this.state.feed = feed;
    this.renderFeed();
    this.renderAvatarWall();
  };

  /* ── Teaser UI ── */
  function DailyTrackerTeaserUI(root, state, api, config) {
    this.root = root;
    this.state = state;
    this.api = api;
    this.config = config;
    this.bindEvents();
  }

  DailyTrackerTeaserUI.prototype.bindEvents = function () {
    var self = this;
    var joinBtn = qs(self.root, '[data-dt-teaser-join-btn]');
    if (joinBtn) {
      joinBtn.addEventListener('click', function () {
        self.handleJoin();
      });
    }
  };

  DailyTrackerTeaserUI.prototype.handleJoin = function () {
    var self = this;
    var nameInput = qs(self.root, '[data-dt-teaser-name]');
    var cityInput = qs(self.root, '[data-dt-teaser-city]');
    var joinBtn = qs(self.root, '[data-dt-teaser-join-btn]');
    var firstName = nameInput ? nameInput.value.trim() : '';
    var city = cityInput ? cityInput.value.trim() : '';
    if (!firstName) {
      if (nameInput) nameInput.focus();
      return;
    }
    if (joinBtn) joinBtn.disabled = true;

    self.api.join(firstName, city || 'India').then(function (res) {
      if (joinBtn) joinBtn.disabled = false;
      if (!res.body.success) {
        showToast(self.root, res.body.error && res.body.error.message || 'Could not join', true);
        return;
      }
      var data = res.body.data;
      DailyTrackerStorage.write({
        userId: data.userId,
        firstName: firstName,
        city: city || 'India',
        joinDate: data.joinDate
      });
      self.state.profile = {
        userId: data.userId,
        firstName: firstName,
        city: city || 'India',
        joinDate: data.joinDate
      };
      self.refresh();
    }).catch(function () {
      if (joinBtn) joinBtn.disabled = false;
      showToast(self.root, networkErrorMessage(self.api.baseUrl), true);
    });
  };

  DailyTrackerTeaserUI.prototype.refresh = function () {
    var self = this;
    self.root.classList.add('is-loading');
    if (!self.state.hydrateFromStorage() || !self.state.profile.userId) {
      self.renderGuest();
      self.root.classList.remove('is-loading');
      return Promise.resolve();
    }

    return self.api.getState(self.state.profile.userId).then(function (res) {
      if (res.status === 404 || !res.body.success) {
        DailyTrackerStorage.clear();
        self.state.profile = null;
        self.renderGuest();
        self.root.classList.remove('is-loading');
        return;
      }
      self.state.applyServerState(res.body.data);
      self.renderMember();
      return self.api.getStats().then(function (statsRes) {
        if (statsRes.body.success) self.state.stats = statsRes.body.data;
        self.renderMember();
        self.root.classList.remove('is-loading');
      }).catch(function (statsErr) {
        console.warn('[DailyTracker] teaser stats failed', statsErr);
        self.root.classList.remove('is-loading');
      });
    }).catch(function (err) {
      console.error('[DailyTracker] teaser refresh failed', err);
      self.root.classList.remove('is-loading');
      showToast(self.root, 'Could not load tracker — check API is running', true, function () {
        self.refresh();
      });
    });
  };

  DailyTrackerTeaserUI.prototype.renderGuest = function () {
    var onboard = qs(this.root, '[data-dt-teaser-onboard]');
    var content = qs(this.root, '[data-dt-teaser-member]');
    if (onboard) onboard.classList.remove('is-hidden');
    if (content) content.classList.add('is-hidden');
  };

  DailyTrackerTeaserUI.prototype.renderMember = function () {
    var onboard = qs(this.root, '[data-dt-teaser-onboard]');
    var content = qs(this.root, '[data-dt-teaser-member]');
    if (onboard) onboard.classList.add('is-hidden');
    if (content) content.classList.remove('is-hidden');

    var streakEl = qs(this.root, '[data-dt-teaser-streak]');
    var dayEl = qs(this.root, '[data-dt-teaser-day]');
    var statusEl = qs(this.root, '[data-dt-teaser-status]');
    var cta = qs(this.root, '[data-dt-teaser-cta]');

    if (streakEl) streakEl.textContent = String(this.state.currentStreak);
    if (dayEl) dayEl.textContent = String(this.state.dayOfRitual());

    if (statusEl) {
      statusEl.classList.remove('is-done');
      if (this.state.loggedToday) {
        statusEl.textContent = '✓ Today\'s ritual complete';
        statusEl.classList.add('is-done');
      } else {
        var partial = DailyTrackerStorage.getPartial();
        if (partial && partial.length) {
          statusEl.textContent = 'In progress — ' + partial.length + ' of 4 logged';
        } else {
          statusEl.textContent = 'Not started — open tracker to log';
        }
      }
    }

    qsa(this.root, '[data-dt-teaser-cta]').forEach(function (cta) {
      if (this.config.fullPageUrl) cta.href = this.config.fullPageUrl;
    }.bind(this));
  };

  DailyTrackerTeaserUI.prototype.updateStats = function (stats) {
    this.state.stats = stats;
  };

  /* ── Pollers ── */
  function DailyTrackerPollers(api, ui, config, mode) {
    this.api = api;
    this.ui = ui;
    this.config = config;
    this.mode = mode;
    this.statsTimer = null;
    this.feedTimer = null;
  }

  DailyTrackerPollers.prototype.start = function () {
    var self = this;
    var statsMs = (self.config.pollStatsSeconds || 60) * 1000;
    var feedMs = (self.config.pollFeedSeconds || 45) * 1000;

    function pollStats() {
      self.api.getStats().then(function (res) {
        if (res.body.success) self.ui.updateStats(res.body.data);
      }).catch(function () { /* ignore */ });
    }

    function pollFeed() {
      if (self.mode !== 'full') return;
      self.api.getFeed(50).then(function (res) {
        if (res.body.success && self.ui.updateFeed) self.ui.updateFeed(res.body.data);
      }).catch(function () { /* ignore */ });
    }

    pollStats();
    self.statsTimer = setInterval(pollStats, statsMs);

    if (self.mode === 'full') {
      pollFeed();
      self.feedTimer = setInterval(pollFeed, feedMs);
    }
  };

  DailyTrackerPollers.prototype.stop = function () {
    if (this.statsTimer) clearInterval(this.statsTimer);
    if (this.feedTimer) clearInterval(this.feedTimer);
  };

  /* ── Init ── */
  function getConfigForRoot(root, index) {
    var instances = (window.DailyTracker && window.DailyTracker.instances) || [];
    var cfg = instances[index] || {};
    return {
      mode: root.getAttribute('data-mode') || cfg.mode || 'full',
      apiBaseUrl: root.getAttribute('data-api-base-url') || cfg.apiBaseUrl || '',
      fullPageUrl: root.getAttribute('data-full-page-url') || cfg.fullPageUrl || '',
      pollStatsSeconds: parseInt(root.getAttribute('data-poll-stats') || cfg.pollStatsSeconds || 60, 10),
      pollFeedSeconds: parseInt(root.getAttribute('data-poll-feed') || cfg.pollFeedSeconds || 45, 10)
    };
  }

  function initRoot(root, index) {
    if (root.getAttribute('data-dt-initialized') === 'true') return;
    root.setAttribute('data-dt-initialized', 'true');

    var config = getConfigForRoot(root, index);
    if (!config.apiBaseUrl) {
      console.warn('[DailyTracker] api_base_url is not configured');
      return;
    }

    var api = new DailyTrackerApi(config.apiBaseUrl);
    var state = new DailyTrackerState();
    state.hydrateFromStorage();

    var mode = config.mode;
    var ui;
    var pollers;

    if (mode === 'teaser') {
      ui = new DailyTrackerTeaserUI(root, state, api, config);
      ui.refresh();
      pollers = new DailyTrackerPollers(api, ui, config, mode);
      pollers.start();
    } else {
      ui = new DailyTrackerFullUI(root, state, api, config);
      if (!state.profile || !state.profile.userId) {
        ui.showOnboard();
      }
      ui.refreshAll();
      pollers = new DailyTrackerPollers(api, ui, config, mode);
      pollers.start();
    }

    root._dtPollers = pollers;
  }

  function boot() {
    var roots = document.querySelectorAll('[data-daily-tracker]');
    roots.forEach(function (root, i) {
      initRoot(root, i);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector('[data-daily-tracker]');
    if (root && root._dtPollers) root._dtPollers.stop();
  });
})();
